import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import { useAuthenticatedRequest } from "./use-authenticated-request";
import { Tenant } from "@/types";

/**
 * Hook to access current tenant information including base currency
 */
export function useTenant() {
  const { user } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();

  const tenantQuery = useQuery({
    queryKey: ["tenant", "settings", user?.activeTenantContext || user?.tenantId],
    queryFn: async (): Promise<Tenant> => {
      const response = await authenticatedRequest('/api/tenant/settings');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tenant settings: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    },
    enabled: !!(user?.activeTenantContext || user?.tenantId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    tenant: tenantQuery.data,
    baseCurrency: tenantQuery.data?.baseCurrency || 'USD',
    isLoading: tenantQuery.isLoading,
    error: tenantQuery.error,
    refetch: tenantQuery.refetch,
  };
}

/**
 * Hook specifically for getting the tenant's base currency
 * Returns USD as fallback if tenant data is not available
 */
export function useTenantCurrency() {
  const { baseCurrency, isLoading } = useTenant();
  
  return { 
    currency: baseCurrency || 'USD',
    isLoading 
  };
}